import {Injectable, OnDestroy} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {StringUtils, UiConfigService} from '@lv1871/core';
import {interval, Observable, of, Subscription, switchMap} from 'rxjs';
import {
  catchError,
  delay,
  filter, take,
  tap
} from 'rxjs/operators';
import {EventService} from './event.service';
import {UiControl} from '../models/uiControl';
import {ErrorService} from '@lv1871/core';
import {
  Adresse,
  flatMap,
  UIArtefakt,
  UiBaseControl
} from '../models/uiArtefakt';
import {UIArtefaktTyp} from '../models/uiArtefaktTyp';
import {UIAdressStatus} from '../models/uiAdressStatus';

@Injectable()
export class AdressValidationService implements OnDestroy {
  private _isPendingSubsciption: Subscription;
  private _isPending: boolean;
  private _uiControl: UiControl;

  constructor(private _http: HttpClient, private _eventService: EventService,
              private _errorService: ErrorService,
              private _uiConfigService: UiConfigService,
  ) {
    this._isPendingSubsciption = this._eventService.uiControlsRequestPending$.subscribe(pending => this._isPending = pending);
    this._eventService.uiControls$.subscribe(uiControl => this._uiControl = uiControl);
    this._eventService.validateAdress$.pipe(
        filter(refIdentifikator => this.getAdressArtefakts(refIdentifikator).every(uiArtefakt => !!uiArtefakt.WertEingabe)),
        filter(refIdentifikator => this._hasAdressChanged(refIdentifikator)),
        tap(refIdentifikator => this._handleBlur(refIdentifikator))
    ).subscribe();
  }

  public handleAdresse(refIdent, adressVorschlagArtefakt: UIArtefakt, uebernehmen) {
    flatMap(this._uiControl.uiVorgang.UIArtefakt).filter(uiArtefakt =>
        !!(uiArtefakt as UiBaseControl).ReferenzIdentifikator
        && (uiArtefakt as UiBaseControl).ReferenzIdentifikator === refIdent).forEach(uiArtefakt => {
          if (uebernehmen) {
            const adressKorrektur = JSON.parse(adressVorschlagArtefakt.WertEingabe);
            Object.keys(adressKorrektur).forEach(key => {
              if (uiArtefakt.FeldnameExtern === key) {
                adressKorrektur[key] = StringUtils.stripHTML(adressKorrektur[key]);
                uiArtefakt.WertEingabe = adressKorrektur[key];
              }
            });
            adressKorrektur.Status = UIAdressStatus.ADRESSE_KORREKT;
            adressVorschlagArtefakt.WertEingabe = JSON.stringify(adressKorrektur);
          } else {
            const adressKorrekturArtefakt = this._getAdressKorrekturArtefakt(refIdent) as Adresse;
            const eingegebeneAdresseMap: Map<String, String> = new Map<String, String>();
            this.getAdressArtefakts(refIdent, this._uiControl).forEach(artefakt => {
              eingegebeneAdresseMap.set(artefakt.FeldnameExtern, artefakt.WertEingabe);
            });
            eingegebeneAdresseMap.set('Status', UIAdressStatus.ADRESSE_KORREKT);
            const eingegebeneAdresseObj = this._mapToObj(eingegebeneAdresseMap);
            adressKorrekturArtefakt.WertEingabe = JSON.stringify(eingegebeneAdresseObj);
          }

    });
    this._eventService.uiControls$.next(this._uiControl);
    this._eventService.uiControlsNewst$.next(this._uiControl);
  }

  public getAdressArtefakts(refIdent, uiControl?: UiControl) {
    if (typeof uiControl == 'undefined') {
      uiControl = this._uiControl;
    }
    return flatMap(uiControl.uiVorgang.UIArtefakt)
    .filter(uiArtefakt =>
        !!(uiArtefakt as UiBaseControl).ReferenzIdentifikator
        && (uiArtefakt as UiBaseControl).ReferenzIdentifikator === refIdent
        && (uiArtefakt as Adresse).IstAdressKomponente);
  }

  public ngOnDestroy() {
    this._isPendingSubsciption.unsubscribe();
  }

  private _handleBlur(uiRefIdent: string) {
    interval(100).pipe(
        delay(350),
        filter(_ => !this._isPending),
        take(1),
        switchMap(_ => this.executePost(uiRefIdent, this._uiControl))
    ).subscribe();
  }


  private executePost(uiRefIdent: string, uiControl: UiControl): Observable<any> {
    const returnMap: Map<String, String> = new Map<String, String>();
    this.getAdressArtefakts(uiRefIdent, uiControl).forEach(artefakt => {
      returnMap.set(artefakt.FeldnameExtern, artefakt.WertEingabe);
    });

    return this._http.post<string>(this._buildAddressUrl(),
        this._mapToObj(returnMap))
    .pipe(
        filter(response => !!response),
        tap((adressVorschlag: string) => {
          this._getAdressKorrekturArtefakt(uiRefIdent).WertEingabe = JSON.stringify(adressVorschlag);
          const copyUiControl = JSON.parse(JSON.stringify(this._uiControl));
          this._eventService.uiControls$.next(copyUiControl);
          this._eventService.uiControlsNewst$.next(copyUiControl);
        }),
        catchError((error) => {
          if (typeof (error.error) === 'string') {
            this._errorService.pushErrorMessage(error.error);
          } else {
            this._errorService.pushErrorMessage(error.message);
          }
          return of(null);
        }));
  }

  private _buildAddressUrl(): string {
    const host = this._uiConfigService.getAntragserfassungUrl();
    return host + '/api/produkt/adress';
  }

  private _getAdressKorrekturArtefakt(refIdent): UIArtefakt {
    const adressKorrekturArtefakt = flatMap(this._uiControl.uiVorgang.UIArtefakt)
    .filter(uiArtefakt =>
        !!(uiArtefakt as UiBaseControl).ReferenzIdentifikator
        && (uiArtefakt as UiBaseControl).ReferenzIdentifikator === refIdent
        && UIArtefaktTyp.ADRESSVORSCHLAG === uiArtefakt.UIArtefakttyp);
    return adressKorrekturArtefakt[0];
  }

  private _hasAdressChanged(uiRefIdent: String): boolean {
    const returnMap: Map<String, String> = new Map<String, String>();
    this.getAdressArtefakts(uiRefIdent, this._uiControl).forEach(artefakt => {
      returnMap.set(artefakt.FeldnameExtern, artefakt.WertEingabe);
    });
    const adressKorrekturArtefakt = this._getAdressKorrekturArtefakt(uiRefIdent) as Adresse;
    let letzteValidierteAdresse = null;
    try {
      letzteValidierteAdresse = JSON.parse(adressKorrekturArtefakt.WertEingabe);
    } catch (e) {
      console?.log(e);
    }
    if (letzteValidierteAdresse?.Status === UIAdressStatus.ADRESSE_KORRIGIERT) {
      return false;
    }
    const eingegebeneAdresse = this._mapToObj(returnMap);
    if (!letzteValidierteAdresse) {
      adressKorrekturArtefakt.WertEingabe = JSON.stringify(eingegebeneAdresse);
      return true;
    } else {
      let adressUnterschiede = [];
      adressUnterschiede = Object.keys(eingegebeneAdresse).filter(key => eingegebeneAdresse[key]+'' !== letzteValidierteAdresse[key]+'');
      adressKorrekturArtefakt.WertEingabe = JSON.stringify(eingegebeneAdresse);
      return !!adressUnterschiede.length;
    }
  }

  private _mapToObj(inputMap: Map<any, any>) {
    const obj = {};
    inputMap.forEach((value, key) => obj[key] = value);
    return obj;
  }
}
